import { useCallback, useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import {
  getSubscriptionPrice,
  LEGACY_PREMIUM_PLAN_1,
  LEGACY_PREMIUM_PLAN_2,
  PREMIUM_COMP_SEC_PLAN_NAME,
  PREMIUM_COMPLIANCE_PLAN_NAME,
} from '../../../../shared/subscriptions';
import { useGetInternalToolsOrgs } from '../../../hooks/api/internalTools/useGetInternalToolsOrgs';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import Button, { BUTTON_TYPE } from '../../generic/Button';
import Flex from '../../generic/Flex';
import TextInput from '../../generic/Forms/TextInput';
import Toggle from '../../generic/Toggle';
import { SearchPrompt } from '../SearchPrompt';
import { useInternalToolsParams } from '../useInternalToolsParams';
import type { InternalToolsOrg, OrgRequestParams } from '../../../../shared/InternalTools/orgs';
import type { InternalToolsParams, ToggleKey } from '../useInternalToolsParams';

import FilteredOrgTable from './FilteredOrgTable';
import OrganizationActions from './OrganizationActions';
import RangedToggle from './RangedToggle';

type OrganizationSearchProps = {
  searchOnly?: boolean;
};

type AndOrSwitchProps = {
  andOrToggle: boolean;
  onToggle: () => void;
};

type ButtonToggleProps = {
  text: string;
  enabled: boolean;
  onClick: () => void;
};

type FilterTogglesProps = {
  filters: InternalToolsParams;
  toggle: (key: ToggleKey) => void;
  setContributorRange: (min: number, max: number) => void;
  setProjectRange: (min: number, max: number) => void;
};

/**
 * Returns true when at least one filter is active or a title search has been entered. The AND/OR
 * toggle on its own is a modifier rather than a filter, so it does not trigger a fetch.
 */
function hasActiveFilter(params: OrgRequestParams): boolean {
  return (
    params.isFree ||
    params.isPremium ||
    params.isPremiumSeat ||
    params.isPremiumSeat2 ||
    params.isPremiumComplianceSeat ||
    params.isPremiumCompSecSeat ||
    params.isCustomBilling ||
    params.contributorCount.enabled ||
    params.projectCount.enabled ||
    params.title.trim() !== ''
  );
}

const AndOrSwitch: VoidFunctionComponent<AndOrSwitchProps> = ({ andOrToggle, onToggle }) => (
  <Flex grow={0.25} align="center" gap={8}>
    <p>OR</p>
    <Toggle checked={andOrToggle} onChange={onToggle} icons={false} />
    <p>AND</p>
  </Flex>
);

const ButtonToggle: VoidFunctionComponent<ButtonToggleProps> = ({ text, enabled, onClick }) => (
  <Button type={enabled ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.OUTLINE} onClick={onClick}>
    {text}
  </Button>
);

const FilterToggles: VoidFunctionComponent<FilterTogglesProps> = ({
  filters,
  toggle,
  setContributorRange,
  setProjectRange,
}) => (
  <div className="wrapped-row">
    <ButtonToggle
      text="free"
      enabled={filters.freeFilterEnabled}
      onClick={() => toggle('freeFilterEnabled')}
    />
    <ButtonToggle
      text="premium"
      enabled={filters.premiumFilterEnabled}
      onClick={() => toggle('premiumFilterEnabled')}
    />
    <ButtonToggle
      text={`$${getSubscriptionPrice(LEGACY_PREMIUM_PLAN_1)}/dev * month (legacy)`}
      enabled={filters.premiumSeatFilterEnabled}
      onClick={() => toggle('premiumSeatFilterEnabled')}
    />
    <ButtonToggle
      text={`$${getSubscriptionPrice(LEGACY_PREMIUM_PLAN_2)}/dev * month (legacy)`}
      enabled={filters.premiumSeat2FilterEnabled}
      onClick={() => toggle('premiumSeat2FilterEnabled')}
    />
    <ButtonToggle
      text={`$${getSubscriptionPrice(PREMIUM_COMPLIANCE_PLAN_NAME)}/dev * month`}
      enabled={filters.premiumComplianceFilterEnabled}
      onClick={() => toggle('premiumComplianceFilterEnabled')}
    />
    <ButtonToggle
      text={`$${getSubscriptionPrice(PREMIUM_COMP_SEC_PLAN_NAME)}/dev * month`}
      enabled={filters.premiumCompSecFilterEnabled}
      onClick={() => toggle('premiumCompSecFilterEnabled')}
    />
    <ButtonToggle
      text="Custom Billing"
      enabled={filters.customBillingFilterEnabled}
      onClick={() => toggle('customBillingFilterEnabled')}
    />
    <RangedToggle
      text="Contributor Count"
      range={filters.contributorCountRange}
      setRange={setContributorRange}
      filterEnabled={filters.contributorCountFilterEnabled}
      toggleFilter={() => toggle('contributorCountFilterEnabled')}
    />
    <RangedToggle
      text="Projects Count"
      range={filters.projectCountRange}
      setRange={setProjectRange}
      filterEnabled={filters.projectCountFilterEnabled}
      toggleFilter={() => toggle('projectCountFilterEnabled')}
    />
  </div>
);

const OrganizationSearch: VoidFunctionComponent<OrganizationSearchProps> = ({
  searchOnly = false,
}) => {
  const {
    params: filters,
    orgRequestParams,
    selectedOrgId,
    toggle,
    setText,
    setContributorRange,
    setProjectRange,
    setSelectedOrgId,
  } = useInternalToolsParams();

  // Mirror the title in local state so fast typing never drops characters while the URL updates.
  const [titleInput, setTitleInput] = useState(filters.title);

  const { debouncedValue: debouncedParams } = useDebouncedValue(orgRequestParams, 500);
  const shouldFetch = useMemo(() => hasActiveFilter(debouncedParams), [debouncedParams]);

  const { data: orgs = [], isFetching } = useGetInternalToolsOrgs(debouncedParams, shouldFetch);

  const selectedOrg = useMemo(
    () => orgs.find((org) => org.id === selectedOrgId) ?? null,
    [orgs, selectedOrgId],
  );

  const handleSelectOrg = useCallback((org: InternalToolsOrg) => {
    setSelectedOrgId(selectedOrgId === org.id ? null : org.id);
  }, [selectedOrgId, setSelectedOrgId]);

  const handleTitleChange = useCallback((value: string) => {
    setTitleInput(value);
    setText(value);
  }, [setText]);

  return (
    <div className="internal-tool-component">
      <div className="org-title-filter">
        {!searchOnly && (
          <AndOrSwitch andOrToggle={filters.andOrToggle} onToggle={() => toggle('andOrToggle')} />
        )}
        <span className="internal-tool-text-input">
          <TextInput
            label="Filter by Title"
            onChange={handleTitleChange}
            value={titleInput}
            placeholder="Filter Organization by Title"
          />
        </span>
      </div>
      <FilterToggles
        filters={filters}
        toggle={toggle}
        setContributorRange={setContributorRange}
        setProjectRange={setProjectRange}
      />
      <div className="margin-top">
        <small style={{ marginBottom: 15 }}>
          *This may only display up to 1000 orgs. To get the full list, either
          search for the name directly or download a csv with the desired
          filters
        </small>
        {shouldFetch ? (
          <FilteredOrgTable
            isSearchOnly={searchOnly}
            orgs={orgs}
            isLoading={isFetching}
            selectedOrg={selectedOrg}
            onSelectOrg={handleSelectOrg}
          />
        ) : (
          <SearchPrompt />
        )}
        {(!searchOnly && selectedOrg) && (
          <OrganizationActions selectedOrg={selectedOrg} orgRequestParams={orgRequestParams} />
        )}
      </div>
    </div>
  );
};

export default OrganizationSearch;
